import { FC, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { TolgeeProvider } from '@tolgee/react';
import { useTypedDispatch, useTypedSelector } from 'students/stores/_utils';
import userSelectors from 'students/stores/user/selectors';
import { userLocaleActions } from 'common/modules/ReactShared';
import I18nContext from '../I18nContext';
import { DEFAULT_LOCALE, RTL_LOCALES } from '../config';
import { tolgee } from '../tolgee';

const I18nProvider: FC<
  PropsWithChildren<{
    overrideLocaleWith?: string;
  }>
> = ({ children, overrideLocaleWith }) => {
  const locale = useTypedSelector(userSelectors.selectUserLocale);
  const localeToUse = overrideLocaleWith || locale || DEFAULT_LOCALE;

  const dispatch = useTypedDispatch();

  useEffect(() => {
    // No-op when the language is already active; on a failed locale-chunk load
    // the previous language stays and t() falls back to en.
    tolgee.changeLanguage(localeToUse).catch(() => undefined);
  }, [localeToUse]);

  const _setLocale = useCallback(
    (_locale: string): void => {
      dispatch(userLocaleActions.updateLocale(overrideLocaleWith || _locale));
      document.documentElement.setAttribute('lang', overrideLocaleWith || _locale);
    },
    [dispatch, overrideLocaleWith]
  );

  useEffect(() => {
    if (RTL_LOCALES.includes(locale)) {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [locale]);

  const contextValue = useMemo(
    () => ({ locale: localeToUse, setLocale: _setLocale }),
    [localeToUse, _setLocale]
  );

  return (
    <TolgeeProvider tolgee={tolgee} options={{ useSuspense: false }}>
      <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
    </TolgeeProvider>
  );
};

export default I18nProvider;
